import { escapeHtml } from './escapeHtml'
import { resolveCurrencySymbol } from './constants'
import type {
  Activity,
  ActivityFanMessage,
  ActivitySubscription,
  ActivityEventAttendance,
  ActivityEventPurchase,
  ActivityPurchase,
  ActivityShortLinkClick,
  ActivityAutomation,
  ActivityProfileDetails,
} from '@/types/resources/activity.ts'
import type { SimpleFan } from '@/types/resources/fan'

/**
 * Compute activity title for display based on activity type and data
 * @param activity Activity object from server
 * @param customer Optional customer context used as fallback when fanPromoterAccount is null
 * @returns HTML string for activity title
 *
 * SECURITY: the returned string is rendered via v-html. Every dynamic value
 * interpolated into the markup MUST be passed through escapeHtml -- fan- and
 * event-supplied strings (names, campaign names, URLs) are stored user input
 * and are a stored-XSS vector otherwise.
 */
export function computeActivityTitle(activity: Activity, customer?: SimpleFan | null, currencySymbol: string = '$'): string {
  if (!activity) return ''

  const { name: fanName, profileUrl: fanProfileUrl } = getFanNameAndUrl(activity, customer)

  switch (activity.activityType) {
    case 'fan-message':
      return getFanMessageTitle(activity)
    case 'data-capture': {
      const { campaignName, captureType } = activity.additionalInfo
      return buildCampaignTitle(campaignName, captureType, activity.campaignOid)
    }
    case 'campaign-registration': {
      const { name, campaignName, captureType } = activity.additionalInfo
      return buildCampaignTitle(campaignName || name, captureType, activity.campaignOid)
    }
    case 'short-link':
    case 'link-activity':
      return `<strong>Clicked on short link</strong> ${escapeHtml(activity.additionalInfo.linkName) || 'short link'}`
    case 'short-link-click':
      return getShortLinkClickTitle(activity)
    case 'website-activity':
      return `<strong>Visited website</strong> ${escapeHtml(activity.additionalInfo.websiteName) || 'website'}`
    case 'subscription':
      return getSubscriptionTitle(activity)
    case 'event-attendance':
      return getEventAttendanceTitle(activity, fanName, fanProfileUrl)
    case 'event-purchase':
    case 'ecommerce-purchase':
      return getPurchaseTitle(activity, currencySymbol)
    case 'automation':
      return getAutomationTitle(activity)
    case 'profile-details':
      return getProfileDetailsTitle(activity)
    default:
      return escapeHtml(activity.activityType) || 'Activity'
  }
}

function getFanNameAndUrl(activity: Activity, customer?: SimpleFan | null): { name: string, profileUrl?: string } {
  const fan = activity.fanPromoterAccount
  // Use fan account for name only if it actually has name data; otherwise fall back to customer
  const nameSource = (fan?.firstName || fan?.lastName || fan?.name) ? fan : customer
  const oid = fan?.oid || customer?.oid

  let name = 'Contact'
  if (nameSource) {
    if (nameSource.firstName || nameSource.lastName) {
      name = `${nameSource.firstName || ''} ${nameSource.lastName || ''}`.trim()
    }
    else if (nameSource.name) {
      name = nameSource.name
    }
    else if (nameSource.emailAddress) {
      name = nameSource.emailAddress
    }
  }

  return {
    name,
    profileUrl: oid ? `/audience/${oid}/view/profile` : undefined,
  }
}

function createLink(url: string, text: string, bold = false): string {
  const classAttribute = bold ? 'class="font-bold"' : ''
  return `<a href="${url}" ${classAttribute}>${text}</a>`
}

function getFanMessageTitle(activity: ActivityFanMessage): string {
  const { taskOid } = activity
  const provider = activity.additionalInfo?.provider
  const campaignCustomerName = activity.additionalInfo?.campaignCustomerName
  const status = activity.additionalInfo?.status
  const automationChartName = activity.additionalInfo?.automationChartName
  const providerLabel = provider === 'email' ? 'Email' : 'SMS'
  const campaignName = escapeHtml(campaignCustomerName) || 'Message'
  const statusMap: Record<string, string> = { sent: 'Sent', clicked: 'Received', opened: 'Received', received: 'Received', bounced: 'Bounced', failed: 'Failed' }
  const statusLabel = statusMap[status || 'sent']
  if (!statusLabel) return campaignName
  const automationSuffix = automationChartName ? ' — via Automation' : ''
  return `${createLink(`/message-center/messages/${taskOid}/view/overview`, `${statusLabel} ${providerLabel}:`)} ${campaignName}${automationSuffix}`
}

function buildCampaignTitle(campaignName: string | undefined, captureType: string | undefined, campaignOid: number | null): string {
  let signUpType = 'Signed up'
  const displayName = escapeHtml(campaignName) || 'Campaign'
  if (captureType?.toLowerCase().includes('presale')) signUpType += ' Presale access'
  else if (captureType?.toLowerCase().includes('waitlist')) signUpType += ' Waitlist'
  const campaignUrl = (campaignOid && campaignName) ? `/campaigns/view/${campaignOid}#dashboard` : ''
  return `<strong>${signUpType}:</strong> ${campaignUrl ? createLink(campaignUrl, displayName) : displayName}`
}

function getShortLinkClickTitle(activity: ActivityShortLinkClick): string {
  const { targetUrl, shortUrl, clickCount = 1 } = activity.additionalInfo
  const url = escapeHtml(targetUrl || shortUrl) || 'link'
  return `<strong>Clicked short link</strong> ${url} (${clickCount} ${clickCount === 1 ? 'click' : 'clicks'})`
}

function getSubscriptionTitle(activity: ActivitySubscription): string {
  const status = activity.additionalInfo?.status
  const actions = activity.additionalInfo?.actions
  const messageBody = activity.additionalInfo?.messageBody
  const provider = activity.additionalInfo?.provider
  const campaignName = activity.additionalInfo?.campaignName
  const messageListOid = activity.additionalInfo?.messageListOid
  const subStatus = status === 'subscribed' ? 'Subscribed to' : 'Unsubscribed from'
  const channelMap = { email: 'Email', sms: 'SMS' } as const
  const channels = actions?.[0]?.channels || []
  const channelText = channels.map(ch => channelMap[ch]).join(' & ')
  const listName = escapeHtml(messageBody) || 'subscription list'
  const sourceMap: Record<string, string> = {
    'preference-center': ' — via Preference center',
    'automation': ' — via Automation',
    'system': ' — via manual update',
  }
  const source = (provider && sourceMap[provider]) || (campaignName ? ` — via ${escapeHtml(campaignName)}` : '')
  const statusText = channelText ? `${subStatus} ${channelText}` : subStatus
  return `${createLink(`/message-center/lists/${messageListOid}/contacts`, statusText, true)} ${listName}${source}`
}

function getEventAttendanceTitle(activity: ActivityEventAttendance, fanName: string, fanProfileUrl?: string): string {
  const eventName = escapeHtml(activity.additionalInfo.eventName) || 'Event'
  const eventUrl = `/events/${activity.eventOid}/view/event#ticket-sales`
  return `${escapeHtml(fanName)} attended ${createLink(eventUrl, eventName)}`
}

function getPurchaseTitle(activity: ActivityEventPurchase | ActivityPurchase, currencySymbol: string): string {
  // The purchase carries the currency it was actually sold in; the promoter's
  // region symbol is only the fallback.
  const symbol = resolveCurrencySymbol(activity.additionalInfo.currencyCode, currencySymbol)
  const { quantity = 1, eventName = 'Event', ticketType = 'General Admission', totalCost, totalAmount, status } = activity.additionalInfo
  const eventUrl = `/events/${activity.eventOid}/view/event`
  const amount = totalCost || totalAmount
  const cost = typeof amount === 'number' ? ` — ${symbol}${(amount / 100).toFixed(2)}` : ''
  const statusMap: Record<string, string> = { cancelled: 'Cancelled', refunded: 'Refunded' }
  const purchaseStatus = (status && statusMap[status]) || 'Purchased'
  return `${createLink(eventUrl, `${purchaseStatus} ${quantity}x ${escapeHtml(ticketType)} tickets`)} to ${createLink(eventUrl, escapeHtml(eventName))}${cost}`
}

function getAutomationTitle(activity: ActivityAutomation): string {
  const { automationName, automationChartName, actionType, messageBody, listName } = activity.additionalInfo
  const automationTitle = escapeHtml(automationName || automationChartName) || 'Automation'
  const actionMap: Record<string, string> = {
    email: `<strong>Sent Email:</strong> ${escapeHtml(messageBody) || 'Email'} — via Automation`,
    subscription: `<strong>Subscribed to</strong> ${escapeHtml(listName) || 'subscription list'} — via Automation`,
    tag: '<strong>Tags added</strong>',
    enrolled: `<strong>Enrolled in Automation:</strong> ${automationTitle}`,
  }
  return (actionType && actionMap[actionType]) || `<strong>Automation:</strong> ${automationTitle}`
}

function getProfileDetailsTitle(activity: ActivityProfileDetails): string {
  const { fieldName = 'Field', oldValue, newValue, source } = activity.additionalInfo
  if (fieldName.toLowerCase().includes('tag')) {
    if (oldValue && !newValue) return '<strong>Tags removed</strong>'
    if (!oldValue && newValue) return '<strong>Tags added</strong>'
    return '<strong>Tags updated</strong>'
  }
  const sourceMap: Record<string, string> = { csv: ' — via csv import', manual: ' — via manual update' }
  return `<strong>${escapeHtml(fieldName)}</strong> updated to "${escapeHtml(newValue)}"${source ? sourceMap[source] || '' : ''}`
}
